if (!customElements.get('sample-link')) {
  customElements.define(
    'sample-link',
    class SampleLink extends HTMLElement {
      constructor() {
        super();
        this.container = this.querySelector('.sample-link__container');
        this.link = this.querySelector('.sample-link__link');
        this.titleSpan = this.querySelector('.sample-link__product-title');
        this.clickHandler = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.link.addEventListener('click', this.clickHandler);
        if (this.initialized) return;
        this.initialized = true;
        this.searchQuery = this.dataset.searchQuery;
        this.isSample = this.dataset.isSample === 'true';
        this.samplePrefix = this.dataset.samplePrefix || 'sample';
        this.currentProductId = this.dataset.currentProductId;
        this.currentProductTitle = this.dataset.currentProductTitle;
        this.relatedProduct = null;

        // On localized (e.g. Arabic) pages, Shopify Markets gives product.handle
        // a localized slug. Detect non-ASCII handles and resolve the English one
        // from the <link rel="alternate" hreflang="en"> tag Shopify injects.
        const handle = this.dataset.productHandle || '';
        if (/[^\x00-\x7F]/.test(handle)) {
          const enLink = document.querySelector('link[rel="alternate"][hreflang="en"]');
          if (enLink) {
            const match = enLink.href.match(/\/products\/([^/?#]+)/);
            if (match) {
              this.dataset.productHandle = match[1];
            }
          }
        }

        if (this.searchQuery) {
          this.fetchRelatedProduct();
        }


      }

      disconnectedCallback() {
        this.link.removeEventListener('click', this.clickHandler);
      }

      handleClick() {
        if (!this.relatedProduct || typeof gtag !== 'function') return;

        gtag('event', 'sample_link_click', {
          link_type: this.isSample ? 'view_original' : 'view_sample',
          current_product_id: this.currentProductId,
          current_product_title: this.currentProductTitle,
          target_product_id: this.relatedProduct.id,
          target_product_title: this.relatedProduct.title,
        });
      }

      async fetchRelatedProduct() {
        try {
          // product.handle is always ASCII/English, e.g. "lattafa-khamrah-qahwa-eau-de-parfum-for-unisex-100ml"
          const handle = this.dataset.productHandle || '';

          // Split handle into words, drop short tokens and size suffixes (100ml, 50ml, 3ml...)
          const handleWords = handle
            .split('-')
            .filter((w) => w.length > 1 && !/^\d+m?l?$/i.test(w) && w.toLowerCase() !== 'sample');

          // Build the API search query
          // - If viewing ORIGINAL → prepend "Sample" + first 3 handle words → finds the sample product
          // - If viewing SAMPLE   → just first 3 handle words (no prefix) → finds the original product
          let apiQuery;
          if (handleWords.length >= 2) {
            const words = handleWords.slice(0, 3).join(' ');
            apiQuery = this.isSample ? words : `${this.samplePrefix} ${words}`;
          } else {
            // No usable handle → clean the Liquid search_query and use it
            apiQuery = this.searchQuery
              .replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, '')
              .split(' ')
              .filter((w) => w.trim().length > 0)
              .slice(0, 4)
              .join(' ');
          }

          const encodedQuery = encodeURIComponent(apiQuery);
          const response = await fetch(
            `/search/suggest.json?q=${encodedQuery}&resources[type]=product&resources[limit]=10&resources[options][fields]=title&locale=en`
          );

          if (!response.ok) throw new Error('Search failed');

          const data = await response.json();
          const products = data.resources?.results?.products || [];

          // Words used to verify the result actually matches this product (not a false positive)
          const verifyWords = handleWords.length >= 2
            ? handleWords.slice(0, 3).map((w) => w.toLowerCase())
            : apiQuery.toLowerCase().split(' ').filter((w) =>
                w !== this.samplePrefix.toLowerCase() && w !== 'sample' && w.length > 1
              ).slice(0, 3);

          // Find the correct related product
          const relatedProduct = products.find((p) => {
            if (String(p.id) === String(this.currentProductId)) return false;

            const titleLower = p.title.replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, '').toLowerCase();

            const isResultSample =
              titleLower.includes(this.samplePrefix.toLowerCase()) ||
              titleLower.includes('\u0639\u064a\u0646\u0629') ||
              titleLower.includes('\u0639\u064a\u0646\u0647') ||
              titleLower.includes('sample');

            // Match the whole fragrance identity, not just a shared brand/name prefix.
          // For example, Fantasy must not link to Midnight Fantasy or Intense.
          const resultHandle = new URL(p.url, window.location.origin).pathname.split('/products/')[1]?.split('/')[0];
          const sourceIdentity = this.productIdentity(handle);
          const targetIdentity = this.productIdentity(resultHandle || '');
          const hasAllWords = sourceIdentity.length > 0 && sourceIdentity === targetIdentity;

            if (this.isSample) {
              // Viewing a sample → find the ORIGINAL (not a sample)
              return !isResultSample && hasAllWords;
            } else {
              // Viewing original → find the SAMPLE
              return isResultSample && hasAllWords;
            }
          });

          if (relatedProduct) {
            this.showLink(relatedProduct);
          } else {
            this.dataset.debugQuery = encodedQuery;
            this.dataset.debugWords = JSON.stringify(verifyWords);
            this.dataset.debugResults = JSON.stringify(products.map((p) => p.title));
            console.log('Sample-link: No match. Query:', apiQuery, '| Words:', verifyWords, '| Results:', products.map((p) => p.title));
          }
        } catch (error) {
          console.error('Sample link search error:', error);
        }
      }

      productIdentity(handle) {
        return decodeURIComponent(handle).toLowerCase()
          .replace(/eau-de-parfum/g, 'edp')
          .replace(/eau-de-toilette/g, 'edt')
          .replace(/(\d+)-?(ml|cl|oz)\b/g, '')
          .split('-')
          .filter((word) => word && !['sample', 'samples', 'vial', 'vials', 'for', 'men', 'women', 'unisex', this.samplePrefix].includes(word))
          .join('-');
      }

      showLink(product) {
        this.relatedProduct = product;
        const targetUrl = new URL(product.url, window.location.origin);
        const root = this.dataset.rootUrl || '/';
        if (targetUrl.pathname.startsWith('/products/')) {
          targetUrl.pathname = root.replace(/\/?$/, '/') + targetUrl.pathname.slice(1);
        }
        this.link.href = targetUrl.pathname + targetUrl.search;
        this.titleSpan.textContent = product.title;
        this.container.classList.remove('hidden');
      }
    }
  );
}
